import React, {SyntheticEvent} from "react";
import {observer} from "mobx-react";
import {autorun, runInAction} from "mobx";

import "./sharing.scss";

import {Form, Button, Divider, Input, Message, Checkbox, Popup as SemanticPopup} from "semantic-ui-react";
import {Icon} from "../icon/icon";
import {CopyToClipboard} from "react-copy-to-clipboard";
import PrintViewModel from "@arcgis/core/widgets/Print/PrintViewModel";
import {PrintConfiguration} from "../../config/print-configuration";
import PrintTemplate from "@arcgis/core/rest/support/PrintTemplate";
import EsriError from "@arcgis/core/core/Error";
import FileLink from "@arcgis/core/widgets/Print/FileLink";
import {PathUtils} from "../../../utils/path-utils";
import {BasemapProps, MapState} from "../../map-state";
import EsriGraphic from "@arcgis/core/Graphic";
import {PopupBase} from "../popup/popup";
import {IAppStore, withStore} from "../../stores/abstract-app-store";
import {AppState} from "../../../components/app/app-mobx";

interface SharingProps {
    config: PrintConfiguration;
    small?: boolean;
    visible: boolean;
    store?: IAppStore; // this is injected by withStore
}

// https://github.com/Esri/arcgis-js-api/blob/bb6824315a3ebb0e23e5cfa7f3319127a4b5a7a6/widgets/Print.tsx
@observer
class SharingBase extends React.Component<SharingProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    widgetKey = "sharing";
    linkHelperElement: HTMLDivElement;
    constructor(props: SharingProps) {
        super(props);
        this.state = {
            viewModel: null,
            screenExtent: null,
            center: {x:0,y:0},
            copied: false,
            imageExported: false,
            mapUrl: "",
            marker: false,
            markerType: "default",
            basemapParam: "",
            markerParam: "",
            scale: 0,
            isLoading: true,
            visible: this.props.visible
        };
        if (typeof this.props.small === "boolean" && this.props.small) {
            this.widgetKey += "-small";
            this.linkHelperElement = document.createElement("div");
            this.linkHelperElement.className = "geo-link-helper";
            this.linkHelperElement.appendChild(document.createElement("div"));
        }
        this.updateMapUrl = this.updateMapUrl.bind(this);
    }

    updateMapUrl(view: __esri.MapView | __esri.SceneView | undefined,
                 marker: boolean, markerType: string): void {
        if (!view) {
            return;
        }

        let focusParam = "";
        if (view?.center) {
            const x = Math.round(view.center.x);
            const y = Math.round(view.center.y);
            // für Esri / Google / Bing tiling Schema Runden
            const scale = Math.round(view.scale / 35) * 35;
            focusParam = `?FOCUS=${x}:${y}:${scale}`;
        }
        this.setState({
            copied: false,
            imageExported: false,
            mapUrl: location.protocol + "//" + location.host + location.pathname + focusParam});
        if (marker) {
            this.drawMarker(markerType);
        }
    }
    updateBasemapParameter(mapStore: MapState, basemap?: BasemapProps | null): void {
        let tag: string | undefined = "";
        if(!basemap && mapStore && mapStore.basemap){
            basemap = mapStore.basemap;
        }
        if(basemap?.tags){
            tag = basemap.tags.find((t) => t.toLowerCase().indexOf("mv6:")===0);
        }
        let basemapParam = "";
        if(tag){
            basemapParam = "&BASEMAP=" + tag.substring(4);
        }
        this.setState({
            basemapParam
        });
    }
    componentDidMount(): void {
        const {config, store} = this.props;
        const {marker, markerType} = this.state;

        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (store?.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }

            if (!store?.mapStore.view) {
                return;
            }
            this.loadPrintViewModel(store.mapStore.view, config, store?.appStore);
            this.updateMapUrl(store.mapStore.view, marker, markerType);
            this.updateBasemapParameter(store.mapStore);
            this.watchHandles.push(store.mapStore.view.watch("center", () => {
                this.updateMapUrl(store.mapStore.view, marker, markerType);
            }));
            this.watchHandles.push(store.mapStore.view.watch("scale", () => {
                this.updateMapUrl(store.mapStore.view, marker, markerType);
            }));
            store.uiStore.addWidget(this.widgetKey, this);
            reaction.dispose();
        }));
        this.autorunDisposers.push(autorun(() => {
            if (!store) {
                return;
            }
            const {basemap} = store.mapStore;
            this.updateBasemapParameter(store.mapStore, basemap);
        }));

    }
    componentDidUpdate(prevProps: SharingProps) {
        const {store} = this.props;
        const {marker, markerType} = this.state;

        if (this.props.visible !== prevProps.visible) {
            this.setState({visible: this.props.visible});
            if(store?.mapStore.view) {
                if(this.props.visible) {
                    this.updateMapUrl(store?.mapStore.view, marker, markerType);
                    this.updateBasemapParameter(store.mapStore);
                    this.watchHandles.push(store.mapStore.view.watch("center", () => {
                        this.updateMapUrl(store.mapStore.view, marker, markerType);
                    }));
                    this.watchHandles.push(store.mapStore.view.watch("scale", () => {
                        this.updateMapUrl(store.mapStore.view, marker, markerType);
                    }));
                } else {
                    this.setState({printScale: null});
                    // TODO: Nur watchHandle für extent entfernen
                    this.watchHandles.forEach((handle: any) => {
                        handle.remove();
                    });
                    this.watchHandles.length = 0;
                }
            }
        }
    }
    componentWillUnmount(): void {
        const {store} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        store?.uiStore.removeWidget(this.widgetKey);
        if(this.linkHelperElement){
            this.linkHelperElement.remove();
        }
    }
    createFileLink(template: PrintTemplate, fileName: string): __esri.FileLink {
        const titleText = fileName || this.state.title;
        const lowercaseFormat = template.format.toLowerCase();
        const extension = lowercaseFormat.indexOf("png") > -1 ? "png" : lowercaseFormat;

        return new FileLink({
            name: "GeoportalLU_" + PathUtils.slug(titleText) + "_" + new Date().toISOString().slice(0,10),
            extension,
            count: 0
        });
    }
    handlePrintMap(event?: SyntheticEvent<HTMLButtonElement>): void {
        const {config} = this.props;
        const {viewModel} = this.state;
        if (event) {
            event.currentTarget.blur();
        }
        const pixelRatio = window.devicePixelRatio || 1;
        const template = new PrintTemplate({
            format: "png32",
            layout: "map-only",
            exportOptions: {
                dpi: this.getPPI(),
                width: Math.round(window.innerWidth * pixelRatio),
                height: Math.round(window.innerHeight * pixelRatio)
            }
        });

        const fileLink = this.createFileLink(template, this.state.title);
        this.setState({exportLink: fileLink, imageExported: false});
        viewModel
            .print(template)
            .then((result: any) => {
                const link = document.createElement("a");
                let pdfUrl = config.exportDirectory + "/";
                pdfUrl += result.url.substring(result.url.lastIndexOf("_") + 1, result.url.lastIndexOf("."));
                pdfUrl = pdfUrl + "/" + this.state.exportLink.formattedName;
                link.href = pdfUrl;
                link.className = "geo-link-helper";
                link.setAttribute("target", "_blank");
                // link.setAttribute("download", this.state.exportLink.formattedName);
                if (this.linkHelperElement) {
                    this.linkHelperElement.appendChild(link);
                }
                link.click();
                this.setState((state: any) => {
                    const {exportLink} = state;
                    exportLink.set({
                        url: pdfUrl,
                        state: "ready"
                    });
                    return {exportLink, imageExported: true};
                });
            })
            .catch((error: EsriError) => {
                console.error(error);
                this.setState((state: any) => {
                    const {exportLink} = state;
                    exportLink.set({
                        error,
                        state: "error"
                    });
                    return { exportLink };
                });
            });
    }
    /* Berechnet die Auflösung des Monitors in DPI
   https://stackoverflow.com/questions/8456455/mobile-web-how-to-get-physical-pixel-size
   https://mobilewidgetdev.wordpress.com/2010/07/20/how-to-really-cope-with-varying-screen-sizes-and-pixel-resolutions-in-vodafone-widgets/
     */
    getPPI(): number {
        // create an empty element
        const div = document.createElement("div");
        // give it an absolute size of one inch
        div.style.width="1in";
        // append it to the body
        const body = document.getElementsByTagName("body")[0];
        body.appendChild(div);
        // read the computed width
        const ppi = document.defaultView?.getComputedStyle(div, null).getPropertyValue("width");
        // remove it again
        body.removeChild(div);
        // and return the value
        return ppi? Math.round(parseFloat(ppi) * (window.devicePixelRatio || 1)): 96;
    }
    loadPrintViewModel(view: __esri.MapView | __esri.SceneView,
                       printConfig: PrintConfiguration,
                       appStore: AppState): void {

        const config = printConfig as __esri.PrintProperties;
        const props = {
            view,
            ...config
        };
        const viewModel = new PrintViewModel(props as __esri.PrintViewModelProperties);
        viewModel.load().then(() => {
            runInAction(() => {
                this.setState({
                    viewModel,
                    title: appStore.topic.label,
                    layout: "MAP_ONLY",
                    isLoading: false
                });
            });
        });
    }
    handleCheckboxChange(event: SyntheticEvent, eventData: any): void {
        event.preventDefault();
        const {store} = this.props;
        const marker = eventData["checked"];
        if(marker){
            const popup = store?.uiStore.widgets.get("popup") as PopupBase;
            if (popup){
                popup.close();
            }
        }
        const markerParam = marker? "&marker"+(this.state.markerType !== "default"? "=" + this.state.markerType: ""):"";
        this.setState({
            marker,
            markerParam
        });
        this.drawMarker(marker? this.state.markerType: "");
    }
    handleMarkerTypeChange(event: SyntheticEvent, eventData: any): void {
        event.preventDefault();
        const markerType = eventData["value"];
        const markerParam = this.state.marker? "&marker"+(markerType !== "default"? "=" + markerType: ""):"";
        this.setState({
            markerType,
            markerParam
        });
        this.drawMarker(markerType);
    }
    drawMarker(markerType: string): void {
        const {store} = this.props;
        if (!store) {
            return;
        }
        const {additionalParameter} = store.mapViewerConfig;
        const marker = markerType !== "";
        let symbol;
        if(store.mapStore.view) {
            store.mapStore.view.graphics.removeAll();
        }
        if(marker && additionalParameter) {
            if (additionalParameter.marker[markerType]) {
                symbol = additionalParameter.marker[markerType];
            } else {
                symbol = store.appConfig.search.resultSymbol?.pointer;
            }
            if (symbol && store.mapStore.view) {
                store.mapStore.view.graphics.push(
                    new EsriGraphic({
                        geometry: store.mapStore.view?.center,
                        symbol
                    })
                );
            }
        }
    }
    render(): JSX.Element | null {
        const {exportLink, isLoading, mapUrl, marker, markerType, markerParam, basemapParam, visible} = this.state;
        if (!visible) {
            return null;
        }
        if (isLoading) {
            return <div className="geo-loading-wrapper"><Icon name={"recycle"} className={"geo-rotating"} /></div>;
        }
        const buttons: JSX.Element[] = [];
        let infoBlock: JSX.Element | null = null;
        if (exportLink) {
            if (exportLink.state==="pending") {
                buttons.push(<Button key={"image"} compact disabled size={"small"}><Icon name={"photo_landscape"} />
                    Bild wird erstellt <Icon name={"recycle"} className={"geo-rotating"} /></Button>);
            } else {
                buttons.push(<Button
                    key={"image"}
                    className={exportLink.state}
                    compact
                    onClick={(e)=> this.handlePrintMap(e)}
                    size={"small"}><Icon name={"photo_landscape"} /> Bild erstellen{
                        (exportLink.state==="error")? <Icon name={"sign_warning"} />: ""}
                </Button>);
                if (exportLink.state==="ready") {
                    buttons.push(<Button
                        key={"download"}
                        compact
                        href={exportLink.url}
                        positive={this.state.imageExported}
                        target={"_blank"}
                        rel={"noreferrer"}
                        size={"small"}><Icon name={"nav_down"} /> Bild öffnen</Button>);
                } else if (exportLink.state==="error") {
                    infoBlock = <Message
                        attached="bottom"
                        className={"error"}
                        content={"Bild erstellen ist fehlgeschlagen. Bitte versuchen Sie es später noch einmal."}
                    />;
                }
            }
        } else {
            buttons.push(<Button
                key={"image"}
                compact
                onClick={(e)=> this.handlePrintMap(e)}
                size={"small"}><Icon name={"photo_landscape"} /> Bild erstellen</Button>);
        }
        return (
            <div className={"sharingWrapper"}>
                <Form size={"small"}>
                    <Form.Field>
                        <label>Link auf aktuelle Karte</label>
                        <Input
                            className={"geo-inputfield-url"}
                            name="url"
                            label={
                                <SemanticPopup
                                    content={this.state.copied ?
                                        <>Link kopiert <Icon name={"check"} /></>: "Link kopieren" }
                                    trigger={
                                        <CopyToClipboard
                                            text={mapUrl + markerParam + basemapParam}
                                            onCopy={() => this.setState({copied: true})}>
                                            <Button
                                                className={this.state.copied ? "success": ""}
                                                compact
                                                positive={this.state.copied}>
                                                <Icon name={"clipboard"} />
                                            </Button>
                                        </CopyToClipboard>} />
                            }
                            labelPosition={"right"}
                            value={mapUrl + markerParam + basemapParam} />
                    </Form.Field>
                    <Form.Field>
                        <Checkbox
                            label={"Kartenzentrum markieren"}
                            checked={marker}
                            onChange={(event: SyntheticEvent, eventData: object) => {
                                this.handleCheckboxChange(event, eventData);
                            }} />
                    </Form.Field>
                    {(marker)? <Form.Field>
                        <Button.Group compact>
                            <Button
                                value={"default"} primary={markerType==="default"}
                                onClick={(event: SyntheticEvent, eventData: object) => {
                                    this.handleMarkerTypeChange(event, eventData);
                                }}><Icon name={"location_pin"} selected /></Button>
                            <Button
                                value={"circle"} primary={markerType==="circle"}
                                onClick={(event: SyntheticEvent, eventData: object) => {
                                    this.handleMarkerTypeChange(event, eventData);
                                }}><Icon name={"shape_circle"} /></Button>
                            <Button
                                value={"point"} primary={markerType==="point"}
                                onClick={(event: SyntheticEvent, eventData: object) => {
                                    this.handleMarkerTypeChange(event, eventData);
                                }}><Icon name={"shape_circle"} selected /></Button>
                            <Button
                                value={"x"} primary={markerType==="x"}
                                onClick={(event: SyntheticEvent, eventData: object) => {
                                    this.handleMarkerTypeChange(event, eventData);
                                }}><Icon name={"navigate_cross"} /></Button>
                            <Button
                                value={"cross"} primary={markerType==="cross"}
                                onClick={(event: SyntheticEvent, eventData: object) => {
                                    this.handleMarkerTypeChange(event, eventData);
                                }}><Icon name={"navigate_plus"} /></Button>
                        </Button.Group></Form.Field>
                        :""}
                    <Divider></Divider>
                    <Form.Field>
                        <label>Karte als Bild speichern</label>
                        {buttons}
                    </Form.Field>
                </Form>
                {infoBlock}
            </div>
        );
    }
}

export const Sharing = withStore(SharingBase);
